import { useOrganization } from "@/contexts/OrganizationContext";

export type OrgRole = 'super_admin' | 'org_admin' | 'performance' | 'medical' | 'coach' | 'player_viewer' | 'parent' | 'nutritionist' | 'physio' | 'analyst' | 'operations';

// Re-export capabilities for centralized access
export { 
  type Capability,
  ROLE_CAPABILITIES,
  ROUTE_CAPABILITIES,
  TABLE_PERMISSIONS,
  roleHasCapability,
  roleHasAllCapabilities,
  roleHasAnyCapability,
  canAccessRoute,
  getAccessibleRoutes,
  isStaffRole,
  isAdminRole,
} from './capabilities';

// Role hierarchy (higher number = more permissions)
const ROLE_HIERARCHY: Record<OrgRole, number> = {
  super_admin: 100,
  org_admin: 80,
  performance: 60,
  medical: 60,
  physio: 58,
  nutritionist: 55,
  analyst: 45,
  coach: 40,
  operations: 35,
  player_viewer: 20,
  parent: 10,
};

/**
 * Hook to get current user's role in the active organization
 */
export function useCurrentRole() {
  const { currentRole, isSuperAdmin, loading } = useOrganization();
  
  return {
    role: currentRole as OrgRole | null,
    isSuperAdmin,
    isLoading: loading,
  };
}

/**
 * Check if user has one of the required roles
 */
export function useHasRole(requiredRoles: OrgRole[]): boolean {
  const { role, isSuperAdmin, isLoading } = useCurrentRole();
  
  // While loading, return false to prevent premature access
  if (isLoading) return false;
  
  // Super admins have all permissions
  if (isSuperAdmin) return true;
  
  // If no role after loading, return false
  if (!role) return false;
  
  // Check if user has any of the required roles
  return requiredRoles.includes(role);
}

/**
 * Check if user's role is at least the required level
 */
export function useRoleAtLeast(requiredRole: OrgRole): boolean {
  const { role, isSuperAdmin, isLoading } = useCurrentRole();
  
  if (isLoading) return false;
  if (isSuperAdmin) return true;
  if (!role) return false;
  
  return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Check if user can manage uploads (org_admin or performance)
 */
export function useCanUpload(): boolean {
  return useHasRole(['org_admin', 'performance']);
}

/**
 * Check if user can create injuries (org_admin, medical, or performance)
 */
export function useCanCreateInjury(): boolean {
  return useHasRole(['org_admin', 'medical', 'performance']);
}

/**
 * Check if user can fully manage injuries - update/delete (org_admin or medical only)
 */
export function useCanManageInjuries(): boolean {
  return useHasRole(['org_admin', 'medical']);
}

/**
 * Check if user can edit wellness data (org_admin, performance, or medical)
 */
export function useCanEditWellness(): boolean {
  return useHasRole(['org_admin', 'performance', 'medical']);
}

/**
 * Check if user can edit performance data (org_admin or performance)
 */
export function useCanEditPerformance(): boolean {
  return useHasRole(['org_admin', 'performance']);
}

/**
 * Check if user can view coach insights
 */
export function useCanViewInsights(): boolean {
  return useHasRole(['org_admin', 'performance', 'medical', 'coach']);
}

/**
 * Check if user can manage organization settings
 */
export function useCanManageOrg(): boolean {
  return useHasRole(['org_admin']);
}

/**
 * Get display name for role
 */
export function getRoleDisplayName(role: OrgRole | null): string {
  if (!role) return 'No Role';
  
  const names: Record<OrgRole, string> = {
    super_admin: 'Super Admin',
    org_admin: 'Organization Admin',
    performance: 'Performance Staff',
    medical: 'Medical Staff',
    coach: 'Coach',
    nutritionist: 'Nutritionist / Dietitian',
    physio: 'Physiotherapist',
    analyst: 'Performance Analyst',
    operations: 'Operations',
    player_viewer: 'Player',
    parent: 'Parent / Guardian',
  };
  
  return names[role];
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role: OrgRole | null): string {
  if (!role) return 'bg-gray-500';
  
  const colors: Record<OrgRole, string> = {
    super_admin: 'bg-purple-500',
    org_admin: 'bg-blue-500',
    performance: 'bg-green-500',
    medical: 'bg-red-500',
    coach: 'bg-orange-500',
    nutritionist: 'bg-green-100 text-green-800',
    physio: 'bg-teal-100 text-teal-800',
    analyst: 'bg-indigo-100 text-indigo-800',
    operations: 'bg-orange-100 text-orange-800',
    player_viewer: 'bg-gray-500',
    parent: 'bg-teal-500',
  };
  
  return colors[role];
}

/**
 * Plain-language description of a role's intent and scope.
 * Used in the Role Permissions settings panel.
 */
export function getRoleDescription(role: OrgRole): string {
  const descriptions: Record<OrgRole, string> = {
    super_admin: 'Platform administrator with full access across all organisations.',
    org_admin: 'Club administrator with full access to all club functions and settings.',
    performance: 'Performance staff with full access to player data, load monitoring, and analytics.',
    medical: 'Medical staff with full access to injury, illness, and clinical data.',
    coach: 'Coaching staff with access to squad management, match data, and player performance.',
    nutritionist: 'Nutrition and dietary staff with access to nutrition logging, wellness data, and recovery monitoring.',
    physio: 'Physiotherapy staff with full access to injury management, RTP protocols, and rehabilitation.',
    analyst: 'Performance analysts with read-only access to all performance and match data.',
    operations: 'Operations and logistics staff with access to scheduling, attendance, and match administration.',
    player_viewer: 'Player account with access to their own data only.',
    parent: "Parent or guardian with access to their linked player's data.",
  };
  return descriptions[role] ?? role;
}

/**
 * Hierarchy value for a role (higher = more authority).
 * Used for display in the Role Permissions matrix.
 */
export function getRoleHierarchy(role: OrgRole): number {
  return ROLE_HIERARCHY[role] ?? 0;
}

